<template>
  <div class="top-line-wrapper" :class="{ 'mt100': loading }">
    <load-content :loading="loading">
      <div class="d-flex justify-content-end">
        <button class="btn btn-sm btn-link w-auto" @click="this.onTranslate" v-tooltip="this.$i18n.t('form.labels.auto_translate')">
          <i class="fas fa-language"></i>
        </button>
      </div>
      <app-form
        id="top-line-form-general"
        :input-fields="fields.general"
        :model="localModel"
        :saveOnBlur="true"
        @updateModel="updateModel"
        @onFormSubmit="saveTopLine"
      />
      <collapsibleFormSection :translatable-title="'top_line.socials'" v-if="isSocialArticle">
        <app-form
          id="top-line-form-socials"
          :input-fields="fields.socials"
          :model="localModel"
          :saveOnBlur="true"
          @updateModel="updateModel"
          @onFormSubmit="saveTopLine"
        />
      </collapsibleFormSection>
      
      <app-form
        id="top-line-form-other"
        :input-fields="fields.other"
        :model="localModel"
        :saveOnBlur="true"
        @updateModel="updateModel"
        @onFormSubmit="saveTopLine"
      />
    </load-content>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import { makeRequest } from '../../helpers/request';
import isEqual from 'lodash/isEqual';
import isEmpty from 'lodash/isEmpty';
import Form from '../form';
import { FIELD_TYPE, VALIDATIONS, MEDIA_TYPES, GENDER, SENTIMENT } from '../../config/const';
import collapsibleFormSection from '../transitions/CollapsibleFormSection';
import LoadContent from '../load-content';

export default {
  props: {
    article: {
      type: Object,
      default: () => { return {} }
    }
  },
  components: {
    'app-form': Form,
    collapsibleFormSection,
    LoadContent
  },

  created: function() {
    this.localModel = this.makeModel(this.article);
    this.getLanguages({ projectId: this.$route.params.project_id });
    this.getCountries({ projectId: this.$route.params.project_id, pagination: { page: 1, perPage: -1, order: 'name', sort: 'asc' } });
  },

  mounted () {
    this.checkAuthorsIsRemote();
    this.selectedAuthors = this.article.authors.map(author => ({ value: author.id, label: this.getAuthorLabel(author) }));
    this.selectedMentions = Array.isArray(this.articleTopLine?.data?.mentions) && this.articleTopLine?.data?.mentions?.reduce((acc, d) => !isEmpty(d) && (acc = [...acc, {
        value: d?.id,
        label: d?.id ? (d?.id?.charAt(0) !== '@' ? '@' : '') + d?.id : (d?.name?.charAt(0) !== '@' ? '@' : '') + d?.name,
      }]) || acc, []) || [];
    this.selectedHashtags = Array.isArray(this.articleTopLine?.data?.hashtags) && this.articleTopLine?.data?.hashtags?.map(m => ({label: m, value: m})) || []
  },

  data () {
    return {
      localModel: {},
      requestData: {},
      selectedAuthors: [],
      remoteSearchAuthors: null,
      selectedMentions: [],
      selectedHashtags: [],
    }
  },

  computed: {
    ...mapGetters({
      languagesOptions: 'languages/languagesOptions',
      countriesOptions: 'countries/countriesOptions',
    }),
    ...mapState({
      projectSetup: state => state.projectSetup.projectSetup.data || {},
      articleTopLine: state => state.articleTopLine.articleTopLine,
      analysisArticle: state => state.analysisArticle.analysisArticle.data,
      editorProjectSettings: state => state.editorProjectSettings.editorProjectSettings.data,
      authors: state => state.authors.authors
    }),
    allowAveForSocial() { return this.projectSetup?.general?.ave_for_social; },
    loading () {
      return !this.authors.data;
    },
    authorsData () {
      return this.authors.data && this.authors.data.data;
    },
    socialsDataEnabled () {
      return this.editorProjectSettings && this.editorProjectSettings.enable_socials_data;
    },
    isSocialArticle() {
      return this.socialsDataEnabled && this.analysisArticle.data && MEDIA_TYPES.SOCIAL.indexOf(this.analysisArticle.data?.outlet?.media_type) > -1;
    },
    authorsOptions() {
      if (!this.authorsData) return [];

      const options = this.authorsData.map(author => ({
        value: author.id,
        label: this.getAuthorLabel(author),
      }));

      return [...options.filter(option => !this.selectedAuthors.find(author => author.value === option.value)), ...this.selectedAuthors];
    },
    mentionsOptions() {
      if (!this.articleTopLine?.data?.mentions) return [];

      const options = Array.isArray(this.articleTopLine?.data?.mentions) && this.articleTopLine?.data?.mentions?.reduce((acc, d) => !isEmpty(d) && (acc = [...acc, {
        value: d?.id,
        label: d?.id ? (d?.id?.charAt(0) !== '@' ? '@' : '') + d?.id : (d?.name?.charAt(0) !== '@' ? '@' : '') + d?.name,
      }]) || acc, []) || [];

      return [...options.filter(option => !this.selectedMentions.find(mention => mention.value === option.value)), ...this.selectedMentions];
    },
    hashtagsOptions() {
      if (!this.articleTopLine?.data?.hashtags) return [];

      const options = Array.isArray(this.articleTopLine?.data?.hashtags) && this.articleTopLine?.data?.hashtags?.reduce((acc, d) => !isEmpty(d) && (acc = [...acc, {
        value: d,
        label: d,
      }]) || acc, []) || [];

      return [...options.filter(option => !this.selectedHashtags.find(ht => ht.value === option.value)), ...this.selectedHashtags];
    },
    authorField() {
      const style = this.isSocialArticle ? 'flex-basis: 50%; padding-right: 10px;' : 'flex-basis: 50%;';
      const label = this.isSocialArticle ? this.$i18n.t('top_line.handle') : this.$i18n.t('top_line.authors.title');
      const placeholder = this.isSocialArticle ? undefined : this.$i18n.t('top_line.authors.select');
      return {
        type: FIELD_TYPE.SELECT,
        label,
        id: 'top-line-authors',
        name: 'authors',
        options: this.authorsOptions,
        multiple: true,
        searchable: true,
        optionsLimit: 100,
        placeholder,
        hideSelected: true,
        closeOnSelected: false,
        trackBy: 'id',
        taggable: true,
        tagPlaceholder: this.$i18n.t('top_line.authors.placeholder'),
        style,
        customCaretClass: this.authors.isBusy ? '' : 'fas fa-user',
        loading: this.authors.isBusy,
        blockGeneralUpdate: true,
        internalSearch: !this.remoteSearchAuthors,
        update: (model) => this.updateArrayOptions(model),
        onSearchChange: (text) => this.remoteSearchAuthors ? this.$emit('searchAuthors', text) : undefined
      }
    },

    aveMultiOptions() {
      if(!this.localModel?.ave_multi) return [];
      let options = [
        ...Object.entries(this.localModel?.ave_multi || {})?.map(([key, value], i) => { return { id: value, label: `${key} | ${value}`, value, $isDisabled: false }; })
      ];
      return options;
    },

    fields() {

      const genderOptions = Object.keys(GENDER).map(g => {
        return {
          value: GENDER[g],
          label: this.$i18n.t('gender.' + GENDER[g])
        }
      });

      const sentimentOptions = Object.keys(SENTIMENT).map(s => {
        return {
          value: SENTIMENT[s],
          label: this.$i18n.t('sentiment.' + SENTIMENT[s])
        }
      })

      const data = {
        general: {
          headline: {
            type: FIELD_TYPE.TEXTAREA,
            label: this.$i18n.t('top_line.headline'),
            id: 'top-line-headline',
            name: 'headline',
            style: 'flex-basis: 100%',
            validate: [VALIDATIONS.REQUIRED]
          },
          translated_headline: {
            type: FIELD_TYPE.INPUT,
            name: 'translated_headline',
            label: this.$i18n.t('top_line.translated_headline'),
            id: 'top-line-translated-headline',
            style: 'flex-basis: 100%'
          },
          date: {
            type: FIELD_TYPE.CALENDAR,
            label: this.$i18n.t('top_line.publish_date'),
            id: 'top-line-date',
            name: 'date',
            inputType: 'date',
            customClass: 'top-line-date',
            inputClass: 'input-ui',
            editable: true,
            clearable: false,
            allowEmpty: true,
            format: 'D MMM YYYY',
            style: 'flex: 0 0 50%; padding-right: 10px;',
            calendarButtonIcon: 'fa fa-calendar',
            validate: [VALIDATIONS.REQUIRED],
            useUtc: true
          },
          ave: {
            type: FIELD_TYPE.INPUT,
            label: this.$i18n.t('top_line.ave'),
            id: 'top-line-ave',
            name: 'ave',
            inputType: 'number',
            decimalPoints: 2,
            step: '0.01',
            style: 'flex: 0 0 50%',
            translate_label_tooltip: this.allowAveForSocial && 'top_line.social_ave',
            validate: [VALIDATIONS.NUMBER],
            showInfoTooltip: !!this.localModel.ave_multi,
            infoTooltipText: this.aveMultiOptions?.map(c => c?.label)?.join(' \n '),
            infoTooltipPosition: 'afterField',
            infoTooltipIconName: 'dollar-sign',
            readOnly: this.allowAveForSocial,
          },
          language: {
            type: FIELD_TYPE.SELECT,
            label: this.$i18n.t('top_line.language.title'),
            id: 'top-line-language',
            name: 'language',
            options: this.languagesOptions,
            multiple: false,
            searchable: true,
            placeholder: this.$i18n.t('top_line.language.select'),
            hideSelected: false,
            closeOnSelected: false,
            trackBy: 'id',
            style: 'flex: 0 0 50%; padding-right: 10px;',
            customCaretClass: 'fas fa-language',
            validate: [VALIDATIONS.REQUIRED]
          },
          impressions: {
            type: FIELD_TYPE.INPUT,
            label: this.$i18n.t('top_line.impressions'),
            id: 'top-line-impressions',
            name: 'impressions',
            inputType: 'number',
            style: 'flex: 0 0 50%',
            validate: [VALIDATIONS.NUMBER],
            readOnly: this.allowAveForSocial,
          },
          refresh_article_metrics: {},
        },
        socials: {
          ...(this.isSocialArticle ? { authors: this.authorField } : {} ),
          gender: {
            type: FIELD_TYPE.SELECT,
            id: 'gender',
            name: 'gender',
            label: this.$i18n.t('top_line.gender'),
            style: 'flex-basis: 50%',
            options: genderOptions
          },
          sentiment: {
            type: FIELD_TYPE.SELECT,
            id: 'sentiment',
            name: 'sentiment',
            label: this.$i18n.t('top_line.sentiment'),
            style: 'flex-basis: 50%; padding-right: 10px;',
            options: sentimentOptions,
            selectedValue: null
          },
          country: {
            type: FIELD_TYPE.SELECT,
            id: 'country',
            name: 'country',
            label: this.$i18n.t('top_line.country'),
            style: 'flex-basis: 50%;',
            options: this.countriesOptions,
            searchable: true
          },
          followers: {
            type: FIELD_TYPE.INPUT,
            id: 'followers',
            name: 'followers',
            label: this.$i18n.t('top_line.followers'),
            style: 'flex-basis: 50%; padding-right: 10px;',
            readOnly: true
          },
          likes: {
            type: FIELD_TYPE.INPUT,
            id: 'likes',
            name: 'likes',
            label: this.$i18n.t('top_line.likes'),
            style: 'flex-basis: 50%;',
            validate: [VALIDATIONS.NUMBER],
            readOnly: true
          },
          no_comments: {
            type: FIELD_TYPE.INPUT,
            id: 'no_comments',
            name: 'no_comments',
            label: this.$i18n.t('top_line.no_comments'),
            style: 'flex-basis: 50%; padding-right: 10px;',
            validate: [VALIDATIONS.NUMBER],
            readOnly: true
          },
          shares: {
            type: FIELD_TYPE.INPUT,
            id: 'shares',
            name: 'shares',
            label: this.$i18n.t('top_line.shares'),
            style: 'flex-basis: 50%;',
            validate: [VALIDATIONS.NUMBER],
            readOnly: true
          },
          mentions: {
            type: FIELD_TYPE.SELECT,
            id: 'mentions',
            name: 'mentions',
            label: this.$i18n.t('top_line.mentions.title'),
            style: 'flex-basis: 50%; padding-right: 10px;',
            options: this.mentionsOptions,
            searchable: true,
            multiple: true,
            taggable: true,
            tagPlaceholder: this.$i18n.t('top_line.mentions.placeholder'),
            hideSelected: true,
            closeOnSelected: false,
            customCaretClass: 'fas fa-at',
            internalSearch: true,
            trackBy: 'id',
            returnObject: true,
            blockGeneralUpdate: true,
            update: (model) => this.updateArrayOptions(model, 'mentions', this.mentionsOptions),
            validate: [],
          },
          hashtags: {
            type: FIELD_TYPE.SELECT,
            id: 'hashtags',
            name: 'hashtags',
            label: this.$i18n.t('top_line.hashtags.title'),
            style: 'flex-basis: 50%;',
            options: this.hashtagsOptions,
            searchable: true,
            multiple: true,
            taggable: true,
            tagPlaceholder: this.$i18n.t('top_line.hashtags.placeholder'),
            hideSelected: true,
            closeOnSelected: false,
            customCaretClass: 'fas fa-hashtag',
            internalSearch: true,
            trackBy: 'id',
            blockGeneralUpdate: true,
            update: (model) => this.updateArrayOptions(model, 'hashtags', this.hashtagsOptions),
            validate: [],
          },
          views: {
            type: FIELD_TYPE.INPUT,
            id: 'views',
            name: 'views',
            label: this.$i18n.t('top_line.views'),
            style: 'flex-basis: 50%; padding-right: 10px;',
            validate: [VALIDATIONS.NUMBER],
            readOnly: true
          },
          reactions: {
            type: FIELD_TYPE.INPUT,
            id: 'reactions',
            name: 'reactions',
            label: this.$i18n.t('top_line.reactions'),
            style: 'flex: 0 0 50%;',
            validate: [VALIDATIONS.NUMBER],
            readOnly: true
          }
        },
        other: {
          ...(!this.isSocialArticle ? { authors: this.authorField } : {} ),
          summary: {
            type: FIELD_TYPE.TEXTAREA,
            id: 'top-line-summary',
            name: 'summary',
            label: this.$i18n.t('top_line.summary'),
            style: 'flex-basis: 100%'
          },
          comments: {
            type: FIELD_TYPE.TEXTAREA,
            id: 'top-line-comments',
            name: 'comments',
            label: this.$i18n.tc('top_line.comment', 2),
            style: 'flex-basis: 100%'
          },
          additional_info: {
            type: FIELD_TYPE.TEXTAREA,
            id: 'top-line-additional-info',
            name: 'additional_info',
            label: this.$i18n.t('top_line.additional_info'),
            style: 'flex-basis: 100%'
          }
        }
      }

      // add refresh metrics button for print
      if (this.localModel.media_type_name === 'print') {
        data.general.refresh_article_metrics = {
          type: FIELD_TYPE.BUTTON,
          id: 'top-line-refresh-article-metrics',
          style: 'flex: 0 0 100%;',
          label_button: this.$i18n.t('top_line.refresh_article_metrics'),
          onClick: () => { 
            this.$emit('refreshArticleTopLineMetrics', {});
          }
        }
      }

      if (!this.localModel.hasOwnProperty('ave')) delete data.general.ave;

      return data;
    }
  },
  
  methods: {
    ...mapActions({
      getLanguages: "languages/getLanguages",
      getCountries: "countries/getCountries",
    }),
    checkAuthorsIsRemote () {
      if (this.remoteSearchAuthors === null && this.authors.data) {
        this.remoteSearchAuthors = this.authors.data.pagination.totalPages > 1;
      }
    },
    onTranslate() {
      // This data is exclusively used here so the request is contained here instead of using the store to avoid extra complexity.
      makeRequest({ url: `/analysis/editor/${this.$route.params.project_id}/translations/${this.$route.params.article_id}`, method: 'post' }).then(r => {
        if (r.data) {
          const { translated_headline, summary } = r.data;
          this.localModel = this.makeModel({ ...this.localModel, translated_headline, summary });
          this.requestData.translated_headline = translated_headline;
          this.requestData.summary = summary;
          this.saveTopLine(false);
        }
      });
    },
    makeFirstModel (article) {
      let model = { ...article };
      model.date = new Date(model.date);              // convert ISO string to instance of Date
      model.date.setDate(model.date.getUTCDate());    // set Date from UTC Date (keeps iso values from article)
      model.date.setHours(model.date.getUTCHours());  // set Hours from UTC Hours (keeps iso values from article)
      return model;                                   // returns model with model.date with the same date and hours
    },
    makeModel (article) {
      return { ...article, date: new Date(article.date) };
    },
    getAuthorLabel (author) {
      const label = [author.first_name, author.last_name, author.job ? '— ['+author.job+']' : ''].filter(text => text).join(' ');
      return label;
    },
    updateArrayOptions (model, key = 'authors', options = this.authorsOptions) {
      if (!model || !model[key]) return;
      const field = this.isSocialArticle ? this.fields.socials?.[key] : this.fields.other?.[key] || this.fields?.general?.[key];
      this[`selected${key?.charAt(0)?.toLocaleUpperCase() + key?.slice(1)}`] = options?.reduce((acc, obj) => {
        const _option = model?.[key]?.find(opt => opt?.value && opt?.value === obj?.value || opt === obj?.value);
        if(_option) {
          obj.label = (obj.label?.charAt(0) !== '@' ? '@' : '') + (obj.label || obj?.value); // ensure label starts with @ (if new added)
          acc = [...acc, obj];
        }
        return acc;
      }, []);
      this.updateModel(model, field);
      this.saveTopLine(true);
    },
    updateModel (model, field) {
      let inputModel = this.makeModel(model);
      if (isEqual(this.localModel, inputModel)) return;
      this.localModel = inputModel;
      const _selectedOptions = this?.['selected' + field.name?.[0]?.toLocaleUpperCase() + field.name?.slice(1)] || [];
      const _shouldReturnObjects = field?.returnObject && Array.isArray(_selectedOptions) && inputModel[field.name]?.length > 0;
      let value = _shouldReturnObjects ? _selectedOptions?.reduce((acc, opt) => {
        if(inputModel[field.name]?.includes(opt?.value)) {
          const newLabel = opt?.label?.charAt(0) === '@' ? opt?.label?.slice(1) : opt?.label; // remove @ symbol if exists
          const name = Array.isArray(this.articleTopLine?.data?.[field.name]) && this.articleTopLine?.data?.[field.name]?.find(o => o.id === opt?.value)?.name || newLabel;
          acc = [...acc, { id: opt?.value, name }];
        }
        return acc;
      }, []) : inputModel[field.name];
      if (field.name === "date") value = value.toISOString().split("T")[0] + "T" + this.article.date.split("T")[1];
      this.requestData[field.name] = value;
    },
    saveTopLine (isAuthors = false, requestSave = true) {
      // If the users updates and switches between fields too fast we lose the input, so we keep the input as a list and update whatever we have
      const fieldsToSave = Object.keys(this.requestData);
      for (let i = 0; i < fieldsToSave.length; i++) {
        const fieldName = fieldsToSave[i];
        const fieldValue = this.requestData[fieldName];
        if (!isEqual(fieldValue, this.article[fieldName])) {
          this.$emit('updateArticleTopLine', { topLineModel: { [fieldName]: fieldValue }, isAuthors, requestSave });
          delete this.requestData[fieldName];
        }
      }
    }
  },
  watch: {
    'article.impressions' (impressions) {
      this.localModel.impressions = impressions;
    },
    'article.ave' (ave) {
        this.localModel.ave = ave;
    },
    remoteSearchAuthors (value) {
      if (value) {
        this.localModel = this.makeModel(this.article);
      }
    },
    authors: {
      handler () {
        this.checkAuthorsIsRemote();
      },
      deep: true
    }
  },
}
</script>

<style lang="scss">
@import "../../styles/components/coding-ui/top-line";
</style>